import type { Option } from "components/core/MultiselectFilterPresentation";
import CalendarToggleTracker from "helpers/CalendarToggleTracker";
import type { FilterOptionsUpdateNotification, ResourceGroupingMapping } from "types";

type CalendarEventData = {
  planting?: number;
  taskType?: string;
  resourceTitle?: string;
  frostType?: string;
};

type CalendarEventExtendedProps = CalendarEventData & {
  [key: string]: unknown;
};

type FullCalendarEvent = {
  extendedProps: CalendarEventExtendedProps;
  setProp: (prop: string, value: string) => void;
};

type CalendarController = {
  calendar: {
    getEvents: () => FullCalendarEvent[];
    prev: () => void;
    next: () => void;
    gotoDate: (date: Date) => void;
    today: () => void;
    getDate: () => Date;
  };
  reload: () => void;
};

export type CalendarElement = Element & {
  year?: CalendarController;
  month?: CalendarController;
};

type EventNotificationCallback = (events: CalendarEventData[]) => void;

type EventNotification = [EventNotificationCallback, number, number];

type LastDraggedEvent = {
  instanceId: string | null;
  start: Date | string | null;
};

class State {
  private lastDraggedEvent: LastDraggedEvent | null = null;

  calendarToggleTracker: CalendarToggleTracker;

  private toNotify: EventNotification[] = [];

  private filterOptionsUpdateSubscriptions: ((
    notification: FilterOptionsUpdateNotification
  ) => void)[] = [];

  private reloading: boolean = false;

  private data: Record<string, CalendarEventData[]> = {};

  fetchQueryParams: string = "";

  plantingLocationFilterOptions: Option[] = [];

  layerGroupFilterOptions: Option[] = [];

  private dirty: boolean = true;

  constructor() {
    this.calendarToggleTracker = new CalendarToggleTracker();
  }

  get selectedPlantingLocationIds() {
    return this.calendarToggleTracker.selectedPlantingLocationIds;
  }

  get selectedLayerGroupIds() {
    return this.calendarToggleTracker.selectedLayerGroupIds;
  }

  subscribeToFilterOptionsUpdate(
    callback: (notification: FilterOptionsUpdateNotification) => void
  ) {
    this.filterOptionsUpdateSubscriptions.push(callback);
  }

  unsubscribeFromFilterOptionsUpdate(
    callback: (notification: FilterOptionsUpdateNotification) => void
  ) {
    this.filterOptionsUpdateSubscriptions = this.filterOptionsUpdateSubscriptions.filter(
      (subscription) => subscription !== callback
    );
  }

  notifyFilterOptionsUpdateSubscribers() {
    const notification: FilterOptionsUpdateNotification = {
      plantingLocationFilterOptions: this.plantingLocationFilterOptions,
      selectedPlantingLocationIds: this.selectedPlantingLocationIds,
      layerGroupFilterOptions: this.layerGroupFilterOptions,
      selectedLayerGroupIds: this.selectedLayerGroupIds
    };

    this.filterOptionsUpdateSubscriptions.forEach((callback) => callback(notification));
  }

  setGroupingMapping(mapping: ResourceGroupingMapping) {
    this.calendarToggleTracker.groupingMapping = mapping;
  }

  setVisiblePlantings(plantingIds: number[]) {
    this.calendarToggleTracker.visiblePlantings = new Set(plantingIds);
  }

  onSelectedPlantingLocationIdsChange(selectedIds: string[]) {
    this.calendarToggleTracker.selectedPlantingLocationIds = selectedIds;

    this.notifyFilterOptionsUpdateSubscribers();
    this.hideOrDisplayAllEvents();
  }

  onSelectedLayerGroupIdsChange(selectedIds: string[]) {
    this.calendarToggleTracker.selectedLayerGroupIds = selectedIds;

    this.notifyFilterOptionsUpdateSubscribers();
    this.hideOrDisplayAllEvents();
  }

  setLastDraggedEvent(instanceId: string | null, start: Date | string | null): void {
    this.lastDraggedEvent = { instanceId, start };
  }

  getLastDraggedEvent(): LastDraggedEvent | null {
    return this.lastDraggedEvent;
  }

  markDirty() {
    this.dirty = true;
  }

  getStoredEvents(year: number, month: number): CalendarEventData[] {
    const key = `${year}-${month}`;
    const events = this.data[key] || [];

    return events;
  }

  async fetchPlantingGroupings(plantingIds: string[]) {
    const url = new URL("/planting_groupings", window.location.origin);

    url.searchParams.append("plantingIds", plantingIds.join(","));

    const resp = await fetch(url);
    const json = await resp.json();

    this.plantingLocationFilterOptions = json.plantingLocations;
    this.layerGroupFilterOptions = json.layerGroups;

    this.setGroupingMapping(json.groupingMapping);
    this.notifyFilterOptionsUpdateSubscribers();
  }

  async reload() {
    if (this.reloading) {
      return;
    }

    this.reloading = true;

    // TODO: can we inject a rails route for the URL?
    const resp = await fetch(
      "/calendar_events/all_by_month_and_year.json?" + this.fetchQueryParams
    );
    this.data = await resp.json();

    this.fetchPlantingGroupings(this.collectPlantingIds());

    this.dirty = false;
    this.reloading = false;
    for (const [successFunc, year, month] of this.toNotify) {
      successFunc(this.getStoredEvents(year, month));
    }
    this.toNotify = [];
  }

  private collectPlantingIds(): string[] {
    return [
      ...new Set(
        Object.values(this.data)
          .flat()
          .map((event) => event.planting?.toString() ?? "")
          .filter((plantingId) => plantingId.length > 0)
      )
    ];
  }

  getCalendarEvents(year: number, month: number, successFunc: EventNotificationCallback) {
    if (this.dirty) {
      // TODO: error handling
      this.reload().catch((_) => undefined);
      this.toNotify.push([successFunc, year, month]);
    } else {
      return successFunc(this.getStoredEvents(year, month));
    }
  }

  getAllCalendarControllers(): CalendarController[] {
    const calendars = document.getElementsByClassName("calendar");
    const controllers: CalendarController[] = [];

    Array.from(calendars).forEach((el) => {
      const element = el as CalendarElement;
      const controller = element.year || element.month;

      if (controller) controllers.push(controller);
    });

    return controllers;
  }

  getAllCalendars(): Array<{ getEvents: () => FullCalendarEvent[] }> {
    return this.getAllCalendarControllers().map((controller) => controller.calendar);
  }

  getAllEventsFromAllCalendars(): FullCalendarEvent[] {
    return this.getAllCalendars().flatMap((calendar) => calendar.getEvents());
  }

  shouldShowCalendarEvent(event: CalendarEventData): boolean {
    const { planting, taskType, resourceTitle } = event;

    if (planting === undefined || taskType === undefined) {
      // eslint-disable-next-line no-console
      console.warn(
        "Event does not have `planting` and/or `taskType` in `State.shouldShowCalendarEvent`",
        event
      );

      return true;
    }

    if (resourceTitle === undefined) {
      // eslint-disable-next-line no-console
      console.warn(
        "Event does not have `resourceTitle` in `State.shouldShowCalendarEvent`",
        event
      );
      return true;
    }

    return (
      this.calendarToggleTracker.isResourceSelectedByFilter(planting) &&
      this.calendarToggleTracker.isResourceTitleVisible(resourceTitle) &&
      this.calendarToggleTracker.isPlantingVisible(planting) &&
      this.calendarToggleTracker.isTaskVisible(taskType)
    );
  }

  private hideOrDisplayAllEvents(): void {
    for (const calEvent of this.getAllEventsFromAllCalendars()) {
      const { planting, taskType, resourceTitle } = calEvent.extendedProps;

      const shouldShow = this.shouldShowCalendarEvent({
        planting,
        taskType,
        resourceTitle
      });

      calEvent.setProp("display", shouldShow ? "auto" : "none");
    }
  }

  showToggle(type: string, value: number) {
    switch (type) {
      case "planting":
        this.calendarToggleTracker.showPlanting(value);
        break;
      default:
        console.error("unknown toggle type", type);
    }
    this.hideOrDisplayAllEvents();
  }

  hideToggle(type: string, value: number) {
    switch (type) {
      case "planting":
        this.calendarToggleTracker.hidePlanting(value);
        break;
      default:
        console.error("unknown toggle type", type);
    }
    this.hideOrDisplayAllEvents();
  }

  setTaskFilter(taskFilter: Record<string, boolean>) {
    this.calendarToggleTracker.setTaskFilter(taskFilter);
    this.hideOrDisplayAllEvents();
  }

  setSearchBox(query: string) {
    this.calendarToggleTracker.setCropNameFilter(query);
    this.hideOrDisplayAllEvents();
  }

  // TODO: move this to another class?
  reloadAllCalendarViews() {
    this.getAllCalendarControllers().forEach((controller) => controller.reload());
  }

  isPlantingVisible(plantingId: number): boolean {
    return this.calendarToggleTracker.isPlantingVisible(plantingId);
  }
}

export const state = new State();

export default State;
